import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useContextStore } from '../hooks/context/useContextStore';
import {
  isImpersonating,
  writeImpersonationSession,
  clearImpersonationSession,
  getAdminOrigin,
  isAllowedAdminOrigin,
  type ImpersonationPayload,
} from '../utils/impersonationSession';
import { clearTokens, getAccessToken, setToken, setRefreshToken } from '../utils/tokenManager';
import { exchangeAccessLinkToken } from '../hooks/auth/useExchangeAccessLinkToken';
import { validateSession } from '@/utils/validateSession';
import { getTokenFromUrl } from '@/utils/urlUtils';
import { logger } from '@/utils/logger';
import { showToast } from '@/utils/toastHelpers';
import { useTranslation } from 'react-i18next';
import { isRedirecting } from '../lib/apollo';
import { emitSessionEvicted } from '../utils/sessionEvictionEvent';
import ServiceUnavailable from './ServiceUnavailable';
import { FullScreenLoader } from './ui/FullScreenLoader';

// Define public routes that don't require authentication
const PUBLIC_ROUTES = [
  '/signin',
  '/signup',
  '/forgot-password',
  '/set-password',
  '/reset-password',
  '/update-profile',
  '/magic',
  '/invite',
  '/accesslink',
  '/',
];

const AUTHENTICATED_PUBLIC_ROUTES_WITH_ACTIONS = [
  '/forgot-password',
  '/invite',
  '/magic',
  '/set-password',
  '/reset-password',
  '/accesslink',
];

/**
 * AuthChecker component
 *
 * Authentication flow:
 * 1. If accessLinkToken is present -> await exchangeAccessLinkToken, redirect if error
 * 2. If token is present -> await userSessionValidation, redirect if error
 * 3. Else check if public/private route
 */
const AuthCheckerInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { signIn } = useContextStore();
  const [error, setError] = useState<string | null>(null);
  const [networkError, setNetworkError] = useState(false);
  const token = getAccessToken();
  const accessLinkToken = getTokenFromUrl('alt');
  const [isAuthenticatingPage, setIsAuthenticatingPage] = useState(
    () => Boolean(token || accessLinkToken) && location.pathname !== '/reset-password'
  );
  // Track if we've already run authentication for this pathname
  const hasRunAuth = useRef<string | null>(null);
  // Whether authentication has resolved once in this app session — not per
  // pathname. SP gates its overlay the same way (`hasCompletedInitialAuth` in
  // its own AuthChecker): the check still runs on every navigation, but the
  // full-screen logo only covers the cold start. It used to raise on every
  // section change, hiding the page the reader had just asked for behind a
  // pulsing logo; now the feature renders straight away and shows its own
  // skeleton while its query is in flight.
  const hasCompletedInitialAuth = useRef(false);

  useEffect(() => {
    const currentPath = location.pathname;

    // Impersonation session active — token is in sessionStorage, not localStorage.
    if (isImpersonating()) {
      hasCompletedInitialAuth.current = true;
      setIsAuthenticatingPage(false);
      return;
    }

    // CRITICAL: Check if apollo.ts is already handling redirect FIRST, before any other logic
    // This prevents AuthChecker from running when apollo.ts has already initiated a redirect
    if (isRedirecting()) {
      hasCompletedInitialAuth.current = true;
      setIsAuthenticatingPage(false);
      logger.info('[AuthChecker] Skipping auth check - apollo.ts is already redirecting', {
        path: currentPath,
      });
      return;
    }

    // Early return if already on signin page to prevent loops
    if (currentPath === '/signin') {
      hasCompletedInitialAuth.current = true;
      setIsAuthenticatingPage(false);
      // Reset the auth check flag when on signin page
      hasRunAuth.current = null;
      return;
    }

    // Prevent running auth check multiple times for the same path
    if (hasRunAuth.current === currentPath) {
      hasCompletedInitialAuth.current = true;
      setIsAuthenticatingPage(false);
      logger.info('[AuthChecker] Skipping - already checked this path', { path: currentPath });
      return;
    }

    const handleAuthentication = async () => {
      hasRunAuth.current = currentPath;
      logger.info('[AuthChecker] Starting authentication check', {
        path: currentPath,
        hasToken: !!token,
        hasAccessLinkToken: !!accessLinkToken,
      });

      try {
        // Step 1: If accessLinkToken is present, await exchangeAccessLinkToken
        if (accessLinkToken) {
          const redirectParam = getTokenFromUrl('redirect') || getTokenFromUrl('returnTo');
          const targetRedirect =
            redirectParam && redirectParam.startsWith('/') && !redirectParam.startsWith('//')
              ? redirectParam
              : '/dashboard';

          try {
            const result = await exchangeAccessLinkToken(accessLinkToken);
            if (!result.success) {
              // Check if it's a network error
              const isNetworkError =
                result.error?.includes('Network') ||
                result.error?.includes('Failed to fetch') ||
                result.error?.includes('connection');

              if (isNetworkError) {
                setNetworkError(true);
                setError(
                  t('unableToConnectToServer') ||
                    'Unable to connect to server. Please check your internet connection and try again.'
                );
                return;
              }

              const rawError = result.error ?? t('authenticationFailed');
              const displayError = rawError.toLowerCase().includes('already been used')
                ? t('thisLinkHasAlreadyBeenUsed') || rawError
                : rawError;

              setError(displayError);
              logger.error({
                message: `[Auth Error]: ${rawError} `,
                context: { path: currentPath, hasToken: !!token },
              });
              showToast(displayError, 'error');

              if (token) {
                hasRunAuth.current = null;
                if (currentPath === '/accesslink' || currentPath === '/set-password') {
                  navigate('/dashboard', { replace: true });
                }
                return;
              }

              clearTokens();
              hasRunAuth.current = null;
              navigate('/signin', { replace: true });
              return;
            }

            if (result.data?.accessToken) {
              const validatedResult = await validateSession(result.data?.accessToken);
              if (validatedResult.user) {
                signIn({
                  token: result.data?.accessToken,
                  refreshToken: result.data?.refreshToken,
                  user: validatedResult.user as ClientUser,
                });
              }
              // Store the tokens
              setToken(result.data.accessToken);
              if (result.data?.refreshToken) {
                setRefreshToken(result.data.refreshToken);
              }

              // Welcome email clicked second: goes into dashboard without any message
              // Verify email clicked second or first: gentle "Your account is verified." message
              const isSubsequentWelcome =
                result.data?.tokenType === 'WELCOME' && result.data?.alreadyVerified;

              if (!isSubsequentWelcome) {
                const message =
                  result.data?.alreadyVerified || result.data?.tokenType === 'VERIFICATION'
                    ? t('yourAccountIsVerified') ||
                      result.data?.message ||
                      'Your account is verified.'
                    : result.data?.message ||
                      t('emailVerifiedSuccessfully') ||
                      'Email verified successfully.';
                if (message) {
                  showToast(message, 'success');
                }
              }

              hasRunAuth.current = null;
              if (currentPath !== '/set-password') {
                navigate(targetRedirect, { replace: true });
              }
              return;
            }
          } catch (error) {
            logger.error({
              message: `[Access link token authentication error]: ${error} `,
              context: { path: currentPath },
            });
            setError(t('failedToAuthenticateAccessToken'));
            if (token) {
              hasRunAuth.current = null;
              if (currentPath === '/accesslink' || currentPath === '/set-password') {
                navigate('/dashboard', { replace: true });
              }
              return;
            }
            hasRunAuth.current = null;
            navigate('/signin', { replace: true });
            return;
          }
        }

        // Step 2: If token is present, await userSessionValidation
        // A reset link establishes its own one-time session. Do not let a stale
        // pre-existing session redirect the user before that exchange completes.
        if (currentPath === '/reset-password') {
          return;
        }

        if (token) {
          const result = await validateSession(token);
          if (!result.success) {
            // Check if it's a network error (API offline/unreachable)
            const isNetworkError =
              result.error?.includes('Network') ||
              result.error?.includes('Failed to fetch') ||
              result.error?.includes('ECONNREFUSED') ||
              result.error?.includes('connection');

            if (isNetworkError) {
              // API is offline - don't redirect, just show error
              setNetworkError(true);
              setError(
                t('unableToConnectToServer') ||
                  'Unable to connect to server. Please check your internet connection and try again.'
              );
              logger.warn(`[Auth Warning]: API unreachable, allowing access to current page`, {
                path: currentPath,
              });
              return;
            }

            // Session was evicted by another device login — show re-auth modal
            // instead of clearing state, so the user can resume their work
            if (result.errorCode === 'SESSION_EVICTED') {
              logger.info('[AuthChecker] Session evicted, showing re-auth modal', {
                path: currentPath,
              });
              emitSessionEvicted();
              return;
            }

            // Not a network error - actual auth failure
            // Check if apollo.ts is already handling the redirect to avoid duplicate redirects
            if (isRedirecting()) {
              logger.info('[AuthChecker] Skipping redirect - apollo.ts is already redirecting', {
                path: currentPath,
              });
              return;
            }
            logger.error({
              message: `[AuthChecker] Session validation failed: ${result.error ?? 'Unknown error'}`,
              context: { path: currentPath, hasToken: !!token },
            });
            clearTokens();
            hasRunAuth.current = null;
            navigate('/signin', { state: { from: location }, replace: true });
            return;
          }

          if (result.user) {
            // If user is on a public route and has valid session, redirect to appropriate page
            if (
              PUBLIC_ROUTES.includes(currentPath) &&
              !AUTHENTICATED_PUBLIC_ROUTES_WITH_ACTIONS.includes(currentPath)
            ) {
              navigate('/dashboard', { replace: true });
            }
            return;
          } else {
            // Check if apollo.ts is already handling the redirect to avoid duplicate redirects
            if (isRedirecting()) {
              logger.info('[AuthChecker] Skipping redirect - apollo.ts is already redirecting', {
                path: currentPath,
              });
              return;
            }
            logger.error({
              message: `[AuthChecker] No user data returned from session validation`,
              context: { path: currentPath },
            });
            clearTokens();
            hasRunAuth.current = null;
            navigate('/signin', { state: { from: location }, replace: true });
            return;
          }
        }

        // Step 3: No tokens - check if public route
        if (PUBLIC_ROUTES.includes(currentPath)) {
          // Reset auth check flag for public routes
          hasRunAuth.current = null;
          return;
        }

        // Check if apollo.ts is already handling the redirect to avoid duplicate redirects
        if (isRedirecting()) {
          // Apollo client is already redirecting, don't duplicate the redirect
          logger.info('[AuthChecker] Skipping redirect - apollo.ts is already redirecting', {
            path: currentPath,
          });
          return;
        }

        // No tokens and not public route - clear all sessions and redirect to sign in
        logger.warn('[AuthChecker] No tokens and not public route, redirecting to sign in', {
          path: currentPath,
        });
        clearTokens();
        // Reset auth check flag before redirect to allow check on signin page
        hasRunAuth.current = null;
        navigate('/signin', { state: { from: location }, replace: true });
      } finally {
        hasCompletedInitialAuth.current = true;
        setIsAuthenticatingPage(false);
      }
    };

    // Only the cold start raises the overlay. Every later navigation runs the
    // same check with the page already on screen.
    setIsAuthenticatingPage(
      !hasCompletedInitialAuth.current &&
        Boolean(accessLinkToken || token) &&
        currentPath !== '/reset-password'
    );
    handleAuthentication();
  }, [location.pathname, accessLinkToken, token, signIn, navigate, t]);

  if (error) {
    logger.error({
      message: `[AuthChecker] Error: ${error}`,
      context: { path: location.pathname },
    });

    // If it's a network error, show ServiceUnavailable page instead of children
    if (networkError) {
      return <ServiceUnavailable onRetry={() => window.location.reload()} />;
    }
  }

  return (
    <>
      <FullScreenLoader isLoading={isAuthenticatingPage} />
      {children}
    </>
  );
};

const IMPERSONATION_HANDSHAKE_TIMEOUT_MS = 10000;

const AuthChecker: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [impersonating, setImpersonating] = useState(isImpersonating);
  const [awaitingImpersonation, setAwaitingImpersonation] = useState(
    () => !isImpersonating() && Boolean(window.opener)
  );
  const navigate = useNavigate();
  const { signIn } = useContextStore();

  // Always listen for messages from admin opener
  useEffect(() => {
    if (!window.opener) return;

    const handler = async (e: MessageEvent) => {
      if (!isAllowedAdminOrigin(e.origin)) return;
      if (e.source !== window.opener) return;

      if (e.data?.type === 'IMPERSONATION_START') {
        const {
          accessToken,
          refreshToken,
          userId,
          userEmail,
          userRole,
          userFirstName,
          userLastName,
          companyName,
          adminName,
        } = e.data as {
          type: string;
          accessToken: string;
          refreshToken: string;
          userId: string;
          userEmail: string;
          userRole: string;
          userFirstName?: string;
          userLastName?: string;
          companyName?: string;
          adminName?: string;
        };

        const sessionPayload: ImpersonationPayload = {
          accessToken,
          refreshToken,
          userId,
          userEmail,
          userRole,
        };
        if (userFirstName !== undefined) sessionPayload.userFirstName = userFirstName;
        if (userLastName !== undefined) sessionPayload.userLastName = userLastName;
        if (companyName !== undefined) sessionPayload.companyName = companyName;
        if (adminName !== undefined) sessionPayload.adminName = adminName;

        writeImpersonationSession(sessionPayload);
        localStorage.removeItem('clientAccessToken');
        localStorage.removeItem('clientRefreshToken');
        localStorage.removeItem('clientUser');

        const validatedResult = await validateSession(accessToken);
        if (validatedResult.user) {
          signIn({
            token: accessToken,
            refreshToken,
            user: {
              ...validatedResult.user,
              analyticsConsent: false,
              isWelcomeScreenClosed: true,
            } as ClientUser,
          });
        }
        setAwaitingImpersonation(false);
        setImpersonating(true);
        navigate('/dashboard', { replace: true });
        return;
      }

      if (e.data?.type === 'END_IMPERSONATION_FROM_ADMIN') {
        clearImpersonationSession();
        setImpersonating(false);
        window.close();
        return;
      }

      if (e.data?.type === 'END_IMPERSONATION_CONFIRMED') {
        clearImpersonationSession();
        setImpersonating(false);
        window.close();
        return;
      }
    };

    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [signIn, navigate]);

  // Initial handshake: signal admin tab only if awaiting initial impersonation
  useEffect(() => {
    if (!awaitingImpersonation || !window.opener) return;

    try {
      const adminOrigin = getAdminOrigin() || '*';
      window.opener.postMessage({ type: 'IMPERSONATION_READY' }, adminOrigin);
    } catch {
      // opener may be cross-origin restricted
    }

    const timeout = window.setTimeout(() => {
      setAwaitingImpersonation(false);
    }, IMPERSONATION_HANDSHAKE_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [awaitingImpersonation]);

  if (impersonating) {
    return <>{children}</>;
  }
  if (awaitingImpersonation) {
    return null;
  }
  return <AuthCheckerInner>{children}</AuthCheckerInner>;
};

export default AuthChecker;
